import { useMemo, useState } from 'react';

import { useI18n } from '@change-corgi/core/react/i18n';
import { useTracking } from '@change-corgi/core/react/tracking';

import { useRealtimeValidation } from '../../../../container/hooks/useRealtimeValidation';
import { useMembershipModalState } from '../../../../modalContext';

type Result = ModelHookResult<
	{
		amount: number;
		value: number;
		currency: string;
		isSelected: boolean;
		currencySymbol: string;
		errorMessage: string | null;
		placeholder: string;
		acceptMembership: boolean;
		amountButtonProps: {
			fontWeight: 'normal' | 'bold';
			textSize: 'large';
			borderWidth: string;
			borderWidthUnselected: string;
			backgroundColor: string;
			backgroundColorUnselected: string;
			color: string;
			colorUnselected: string;
			buttonHeight: `${number}px`;
		};
	},
	{
		onClick: () => void;
		onChange: (value: string) => void;
		onFocus: () => void;
	}
>;

// eslint-disable-next-line max-lines-per-function
export function useAmountSelector(): Result {
	const [{ amount, currencyCode, validationErrors, koiPpmuDesignExperimentVariant, acceptMembership }, { setAmount }] =
		useMembershipModalState();
	const { getCurrencySymbol, translate } = useI18n();

	const [isCustomAmount, setIsCustomAmount] = useState<boolean>(false);
	const [customAmount, setCustomAmount] = useState<number>(0);

	const track = useTracking();

	useRealtimeValidation();

	const originalAmount = useMemo(() => {
		return amount;
		// This is intentional, we only want to run this once to store initial amount
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const amountButtonProps = {
		fontWeight: koiPpmuDesignExperimentVariant === 'variant_3' ? 'bold' : 'normal',
		textSize: 'large',
		borderWidth: koiPpmuDesignExperimentVariant === 'variant_3' ? '2px' : '1px',
		borderWidthUnselected: '1px',
		backgroundColor:
			koiPpmuDesignExperimentVariant === 'variant_3'
				? 'sunrise.semantic.fill.accent.primary.weaker'
				: 'sunrise.semantic.fill.accent.weaker',
		backgroundColorUnselected: 'sunrise.semantic.fill.inverse.strong',
		color:
			koiPpmuDesignExperimentVariant === 'variant_3'
				? 'sunrise.semantic.typography.strong'
				: 'sunrise.semantic.typography.accent.primary',
		colorUnselected: 'sunrise.semantic.typography.strong',
		buttonHeight: koiPpmuDesignExperimentVariant === 'variant_3' ? '40px' : '50px',
	} as const;

	return {
		data: {
			amount: originalAmount,
			currency: currencyCode,
			isSelected: !isCustomAmount,
			errorMessage: customAmount !== 0 ? (validationErrors?.amount ?? null) : null,
			value: isCustomAmount ? amount : 0,
			currencySymbol: getCurrencySymbol(currencyCode),
			placeholder: translate('corgi.co.promote.custom_amount'),
			amountButtonProps,
			acceptMembership,
		},
		actions: {
			onClick: () => {
				setAmount(originalAmount);
				setCustomAmount(0);
				setIsCustomAmount(false);
				void track('post_promote_default_amount_selected');
			},
			onFocus: () => {
				setAmount(customAmount);
				setIsCustomAmount(true);
				void track('post_promote_other_amount_selected');
			},
			onChange: (value) => {
				// Only update `amount` if the new input value is numeric. Otherwise parsing the string would yield NaN, which would
				// clear `amount` and be a bad/surprising UX
				if (/^\d*$/.test(value)) {
					const newAmount = Number(value);
					setAmount(newAmount);
					setCustomAmount(newAmount);
				}
			},
		},
	};
}
